import { useEffect } from "react";

/**
 * BW19 PR-B / HOTFIX-BW19.1 Bug 1: single-key "n" opens the global capture
 * modal (Linear/Things pattern). Browsers hardwire Cmd+Shift+N to "new
 * private window", so a modifier-based shortcut is unreachable from a PWA.
 * Fires only when no input/textarea/select/contenteditable is focused and no
 * modifier keys are held, so we never steal a keystroke from an active form.
 */

export function useCaptureShortcut(onTrigger: () => void): void {
  useEffect(() => {
    function isTextEntry(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (target.isContentEditable) return true;
      return false;
    }

    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (e.key !== "n" && e.key !== "N") return;
      const active = document.activeElement;
      if (isTextEntry(e.target) || isTextEntry(active)) return;
      e.preventDefault();
      onTrigger();
    }

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onTrigger]);
}
